import React from 'react';
import isEmpty from 'lodash/isEmpty';
import Papa from 'papaparse';
import { Modal, MODAL_SIZES } from 'ui/modal';
import noop from 'utils/noop';
import { UPDATE_CATALOG_ITEM_METHOD } from 'modules/catalogTable/constants';
import ModalHeader from './ModalHeader';
import ModalActions from './ModalActions';
import ModalContent from './ModalContent';
import {
  doColsNotHaveKey,
  doColsNotHaveValues,
  getPreviewTableColumns,
  getHandleOnChangeFieldTypesDropdownState,
  getHandleStepAndSkipState,
  getNewFieldsFromCsv,
  getRows,
  checkIfIsDisabled,
} from './CatalogFormCSV.utils';

// type Props = {
//   /** Callback if csv key is missing. */
//   csvKeyMissing: () => void,
//   /** Function when closing modal, triggers fetch to update list */
//   closeUpdateItemModal: () => void,
//   /** Is update catalog modal open or not */
//   updateCatalogIsOpen: boolean,
//   /** String for current table */
//   tableName: string,
//   /** Boolean for CatalogQueries flag */
//   isCatalogCollectionsFlagOn: boolean,
//   /** Is form in process of being submitted or not (saved in redux store) */
//   isSubmitting: ?boolean,
//   /** Function to call to bulk update/create catalog items via CSV */
//   updateBulkCatalogItemsRequest: (*) => void,
//   /** Function to call to update field types and then bulk update/create catalog items via CSV */
//   updateFieldsAndBulkCatalogItemsRequest: (*) => void,
//   /** Callback if upload size of file exceeded. */
//   uploadSizeExceeded: () => void,
//   /** Object representing defined fields where key is field name and value is field type e.g., "string", "geo_location" */
//   definedFields: Object,
// };

// type State = {
//   csvFile: any,
//   csvRowData: Array<*>,
//   csvColumnData: Array<*>,
//   csvFileTimeStamp: ?Date | ?number,
//   keyWarning: boolean,
//   headerValueWarning: boolean,
//   csvFileError: ?string,
//   csvFileName: ?string,
//   step: number,
//   skipDefine: boolean,
//   newCsvFields: Array<*>,
//   newJsonFields: Array<*>,
//   fields: Object,
//   isSubmitting: ?boolean,
//   suggestedTypes: ?Object,
//   nestedFieldsWarning: boolean,
// };

// this is arbitrary. need to talk with backend about how to handle this
const MAX_BYTES = 30000000;
const MAX_TIME = 1000 * 60;
const INIT_STATE = {
  csvFile: null,
  csvRowData: [],
  csvColumnData: [],
  csvFileTimeStamp: null,
  keyWarning: false,
  headerValueWarning: false,
  csvFileError: null,
  csvFileName: null,
  step: 1,
  skipDefine: false,
  newCsvFields: [],
  newJsonFields: [],
  fields: {},
  nestedFieldsWarning: false,
};

export default class CatalogFormCSV extends React.Component {
  static defaultProps = {
    csvKeyMissing: noop,
    uploadSizeExceeded: noop,
  };

  constructor(props) {
    super(props);
    this.state = {
      ...INIT_STATE,
    };
  }

  getNewFieldsFromCsv = (meta) => {
    return getNewFieldsFromCsv(this.props, meta);
  };

  getRows = getRows;

  onCsvPreview = (files) => {
    const reader = new FileReader();

    reader.onloadstart = (evt) => {
      this.setState({ csvFileError: null });
      // abort if too big
      if (evt.total > MAX_BYTES) {
        this.props.uploadSizeExceeded();
        this.setState({ csvFileError: 'This file is too large.' });
        reader.abort();
      } else {
        this.setState({
          csvFileTimeStamp: evt.timeStamp,
        });
      }
    };

    reader.onprogress = (evt) => {
      // abort if it's taking too long
      if (evt.timeStamp - this.state.csvFileTimeStamp > MAX_TIME) {
        this.props.uploadSizeExceeded();
        this.setState({
          csvFileError: 'Reading this file is taking too long. Try uploading a smaller CSV',
        });
        reader.abort();
      }
    };

    reader.onabort = () => {
      this.setState({
        csvFile: null,
        csvRowData: [],
        csvColumnData: [],
        csvFileTimeStamp: null,
        keyWarning: false,
        headerValueWarning: false,
        csvFileName: null,
      });
    };

    reader.onerror = (evt) => {
      this.setState({ csvFileError: evt.target.error.code });
      reader.abort();
    };

    reader.onload = () => {
      this.setState({ csvFileTimeStamp: null });
      const { result } = reader;
      // Replace zero width characters with new line.
      const formattedResult = result.replace(/\u2028|\u2029/g, '\n');
      const parsed = Papa.parse(formattedResult, { preview: 5, header: true });
      // update rowData and columnData
      const cols = getPreviewTableColumns(parsed.meta.fields);
      const rows = this.getRows(parsed.data);
      // check for new fields
      const newCsvFields = this.getNewFieldsFromCsv(parsed.meta);

      this.setState({
        csvColumnData: cols,
        csvRowData: rows,
        keyWarning: this.doColsNotHaveKey(cols),
        headerValueWarning: this.doColsNotHaveValues(cols),
        csvFileName: files[0].name,
        newCsvFields,
      });
    };

    // this block also fires when the file is changed
    // or when the file is cleared in the UI. In that second case, files will be an
    // empty array, and we need to deal with that appropriately by resetting the state.
    if (files?.length > 0) {
      reader.readAsText(files[0]);
      this.setState({ csvFile: files[0] });
    } else {
      this.setState({
        csvFile: null,
        csvRowData: [],
        csvColumnData: [],
        csvFileTimeStamp: null,
        keyWarning: false,
        headerValueWarning: false,
        csvFileName: null,
      });
    }
  };

  doColsNotHaveKey = (cols /*: Array<Object> */) => {
    return doColsNotHaveKey(this.props, cols);
  };

  doColsNotHaveValues = doColsNotHaveValues;

  handleSubmit = () => {
    const { fields } = this.state;
    const { tableName } = this.props;
    const file = new FormData();
    file.append('file', this.state.csvFile);
    if (isEmpty(fields)) {
      // only update catalog items
      this.props.updateBulkCatalogItemsRequest({ ...this.state, tableName, file });
    } else {
      // update fields and items
      this.props.updateFieldsAndBulkCatalogItemsRequest({ ...this.state, tableName, file });
    }
    this.handleCloseModal();
  };

  handleCloseModal = () => {
    this.setState({
      ...INIT_STATE,
    });
    this.props.closeUpdateItemModal();
  };

  handleStepAndSkip = (step /*: number */, skipDefine /*: ?boolean */) => {
    const stateToUpdate = getHandleStepAndSkipState(this.state, step, skipDefine);
    this.setState(stateToUpdate);
  };

  isDisabled = () => checkIfIsDisabled(this.state);

  handleOnChangeFieldTypesDropdown = (val, key) => {
    const stateToSet = getHandleOnChangeFieldTypesDropdownState(this.state, val, key);
    this.setState(stateToSet);
  };

  render() {
    const {
      csvRowData,
      csvColumnData,
      keyWarning,
      headerValueWarning,
      csvFileName,
      csvFileError,
      step,
      skipDefine,
      newCsvFields,
      fields,
      suggestedTypes,
      nestedFieldsWarning,
    } = this.state;
    const { tableName, isCatalogCollectionsFlagOn, updateCatalogIsOpen } = this.props;
    const isDisabled = this.isDisabled();
    const steps = [
      {
        label: 'Select file',
        onClick: () => this.handleStepAndSkip(1, skipDefine),
      },
      {
        label: 'Define fields',
        onClick: () => this.handleStepAndSkip(2, false),
      },
      {
        label: 'Upload',
        icon: 'flag checkered',
        onClick: () => this.handleStepAndSkip(3, skipDefine),
      },
    ];

    return updateCatalogIsOpen ? (
      <Modal
        size={MODAL_SIZES.LARGE}
        id="catalogFormCsvModal"
        isModalOpen={updateCatalogIsOpen}
        onClose={this.handleCloseModal}
      >
        <ModalHeader
          tableName={tableName}
          isCatalogCollectionsFlagOn={isCatalogCollectionsFlagOn}
          steps={steps}
          step={step}
          skipDefine={skipDefine}
          updateMethod={UPDATE_CATALOG_ITEM_METHOD.CSV}
        />
        <ModalContent
          isCatalogCollectionsFlagOn={isCatalogCollectionsFlagOn}
          steps={steps}
          step={step}
          skipDefine={skipDefine}
          updateMethod={UPDATE_CATALOG_ITEM_METHOD.CSV}
          csvRowData={csvRowData}
          csvColumnData={csvColumnData}
          onCsvPreview={this.onCsvPreview}
          keyWarning={keyWarning}
          headerValueWarning={headerValueWarning}
          nestedFieldsWarning={nestedFieldsWarning}
          csvFileName={csvFileName}
          csvFileError={csvFileError}
          newFields={newCsvFields}
          fields={fields}
          suggestedTypes={suggestedTypes}
          dropdownOnChangeHandler={this.handleOnChangeFieldTypesDropdown}
        />
        <ModalActions
          isCatalogCollectionsFlagOn={isCatalogCollectionsFlagOn}
          isDisabled={isDisabled}
          handleStepAndSkip={this.handleStepAndSkip}
          handleSubmit={this.handleSubmit}
          step={step}
          skipDefine={skipDefine}
          fields={fields}
          hasNewFields={newCsvFields.length > 0}
          nestedFieldsWarning={nestedFieldsWarning}
        />
      </Modal>
    ) : null;
  }
}
